const CHAT_STATE_VERSION = 1;
const CHAT_STATE_KEY_PREFIX = "customer_chat_state_";

export const CUSTOMER_CHAT_HOME_DELAY_MS = 10 * 1000;
export const CUSTOMER_CHAT_BACKGROUND_COOLDOWN_MS = 30 * 1000;

const getStorage = () => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
};

const normalizeId = (value) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue) || numberValue <= 0) {
    return 0;
  }
  return Math.floor(numberValue);
};

const getPlayerIdentity = (userInfo) => {
  if (!userInfo) {
    return "";
  }
  const identity =
    userInfo.rid ||
    userInfo.id ||
    userInfo.uid ||
    userInfo.player_id ||
    userInfo.playerId ||
    userInfo.role_account ||
    userInfo.account ||
    userInfo.token;
  return identity === null || typeof identity === "undefined" ? "" : String(identity);
};

export const shouldUseAnonymousCustomerChat = (userInfo) => !(userInfo && userInfo.token);

export const getCustomerChatStateKey = (userInfo, useAnonymous) => {
  if (useAnonymous) {
    return `${CHAT_STATE_KEY_PREFIX}anonymous`;
  }
  const identity = getPlayerIdentity(userInfo);
  return identity ? `${CHAT_STATE_KEY_PREFIX}${encodeURIComponent(identity)}` : "";
};

export const getCustomerChatState = (stateKey) => {
  const defaultState = { lastMsgId: 0, unread: false, lastBackgroundRequestAt: 0 };
  const storage = getStorage();
  if (!storage || !stateKey) {
    return defaultState;
  }
  try {
    const storedState = JSON.parse(storage.getItem(stateKey) || "null");
    if (!storedState || storedState.version !== CHAT_STATE_VERSION) {
      return defaultState;
    }
    return {
      lastMsgId: normalizeId(storedState.last_msg_id),
      unread: storedState.unread === true,
      lastBackgroundRequestAt: normalizeId(storedState.last_background_request_at),
    };
  } catch (error) {
    return defaultState;
  }
};

const writeCustomerChatState = (stateKey, state) => {
  const storage = getStorage();
  if (!storage || !stateKey) {
    return false;
  }
  try {
    storage.setItem(
      stateKey,
      JSON.stringify({
        version: CHAT_STATE_VERSION,
        last_msg_id: normalizeId(state.lastMsgId),
        unread: state.unread === true,
        last_background_request_at: normalizeId(state.lastBackgroundRequestAt),
      })
    );
    return true;
  } catch (error) {
    return false;
  }
};

export const saveCustomerChatLatestId = (stateKey, latestMsgId, markUnread = false) => {
  const currentState = getCustomerChatState(stateKey);
  const normalizedLatestMsgId = normalizeId(latestMsgId);
  const isNew = normalizedLatestMsgId > currentState.lastMsgId;
  const nextState = {
    lastMsgId: Math.max(currentState.lastMsgId, normalizedLatestMsgId),
    unread: currentState.unread || (markUnread && isNew),
    lastBackgroundRequestAt: currentState.lastBackgroundRequestAt,
  };

  if (stateKey && (isNew || nextState.unread !== currentState.unread)) {
    writeCustomerChatState(stateKey, nextState);
  }
  return { ...nextState, isNew, previousLastMsgId: currentState.lastMsgId };
};

export const markCustomerChatRead = (stateKey) => {
  const currentState = getCustomerChatState(stateKey);
  if (stateKey && currentState.unread) {
    writeCustomerChatState(stateKey, { ...currentState, unread: false });
  }
  return { ...currentState, unread: false };
};

export const markCustomerChatBackgroundRequest = (stateKey, requestedAt = Date.now()) => {
  const currentState = getCustomerChatState(stateKey);
  const nextState = {
    ...currentState,
    lastBackgroundRequestAt: normalizeId(requestedAt),
  };
  if (stateKey) {
    writeCustomerChatState(stateKey, nextState);
  }
  return nextState;
};

export const getCustomerChatBackgroundDelay = (lastRequestAt, now = Date.now()) => {
  const elapsed = Math.max(0, now - (Number(lastRequestAt) || 0));
  const cooldownRemaining = Math.max(0, CUSTOMER_CHAT_BACKGROUND_COOLDOWN_MS - elapsed);
  return Math.max(CUSTOMER_CHAT_HOME_DELAY_MS, cooldownRemaining);
};

export const isCustomerChatMainInterfacePath = (pathname) =>
  typeof pathname === "string" && pathname !== "/game" && !pathname.startsWith("/game/");

export const isBroadcastMessage = (message) => {
  if (!message) {
    return false;
  }
  let extra = message.extra;
  if (typeof extra === "string") {
    try {
      extra = JSON.parse(extra);
    } catch (error) {
      return false;
    }
  }
  if (!extra || typeof extra !== "object") {
    return false;
  }
  return extra.from_broadcast === true || extra.from_broadcast === 1 || extra.from_broadcast === "1";
};

export const getBroadcastMessagePreview = (message, maxLength = 48) => {
  const content = String((message && message.content) || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!content) {
    return "You have a new message";
  }
  if (content.length <= maxLength) {
    return content;
  }
  return `${content.slice(0, maxLength).trim()}...`;
};
